// src/components/EarningsPanel.tsx
import React from 'react';
import { Transaction } from '../types';

interface EarningsPanelProps {
  transactions: Transaction[];
  jwt: string | null;
}

const toNum = (v: unknown): number => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const shortHash = (hash?: string, left = 6, right = 6) => {
  if (!hash) return '';
  if (hash.length <= left + right + 3) return hash;
  return `${hash.slice(0, left)}...${hash.slice(-right)}`;
};

const txExplorerUrl = (chain?: string, txHash?: string) => {
  const c = (chain || '').toLowerCase();
  if (!txHash) return '#';
  if (c === 'xch') return `https://xchscan.com/txns/${txHash}`;
  if (c === 'fil') return `https://filscan.io/en/message/${txHash}`;
  if (c === 'ar') return `https://viewblock.io/arweave/tx/${txHash}`;
  // fallback (np. BSC)
  return `https://bscscan.com/tx/${txHash}`;
};

const EarningsPanel: React.FC<EarningsPanelProps> = ({ transactions, jwt }) => {
  const totalXCH = (transactions || [])
    .filter((tx) => tx.chain?.toLowerCase() === 'xch' && tx.direction === 'in')
    .reduce((sum, tx) => sum + toNum(tx.amount), 0);

  const totalFIL = (transactions || [])
    .filter((tx) => tx.chain?.toLowerCase() === 'fil' && tx.direction === 'in')
    .reduce((sum, tx) => sum + toNum(tx.amount), 0);

  const totalAR = (transactions || [])
    .filter((tx) => tx.chain?.toLowerCase() === 'ar' && tx.direction === 'in')
    .reduce((sum, tx) => sum + toNum(tx.amount), 0);

  if (!jwt) {
    return (
      <div className="p-8 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center border border-amber-500/30">
          <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Authentication Required</h3>
        <p className="text-gray-300 mb-2">Please sign in to view your earnings</p>
        <p className="text-gray-400 text-sm">Go to Account Overview tab to log in with your wallet</p>
      </div>
    );
  }

  const needsScroll = (transactions || []).length > 10;

  return (
    <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-md border border-gray-700/50 p-6 rounded-xl shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg">
💰
        </div>
        <h3 className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
          Your Earnings
        </h3>
      </div>

      {/* Earnings Summary */}
      <div className="space-y-4 mb-8">
        <h4 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
          <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Total Rewards Earned
        </h4>

<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  {/* XCH */}
  <div className="p-5 bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl border border-green-500/30 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-green-400/50">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-green-500/20 rounded-lg">
          <img
            src="/xch.webp"
            alt="Chia (XCH) logo"
            className="w-5 h-5 rounded-full object-cover ring-2 ring-green-400/60 bg-white/5"
            loading="lazy"
            decoding="async"
            width="20"
            height="20"
          />
        </div>
        <span className="text-sm font-medium text-green-300">Chia (XCH)</span>
      </div>
    </div>
    <p className="text-3xl font-bold text-green-400 mb-1">{totalXCH.toFixed(6)}</p>
    <p className="text-xs text-green-300/70">Chia Network Rewards</p>
  </div>

  {/* FIL */}
  <div className="p-5 bg-gradient-to-br from-purple-900/30 to-violet-900/30 rounded-xl border border-purple-500/30 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-purple-400/50">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <img
            src="/filecoin.webp"
            alt="Filecoin (FIL) logo"
            className="w-5 h-5 rounded-full object-cover ring-2 ring-purple-400/60 bg-white/5"
            loading="lazy"
            decoding="async"
            width="20"
            height="20"
          />
        </div>
        <span className="text-sm font-medium text-purple-300">Filecoin (FIL)</span>
      </div>
    </div>
    <p className="text-3xl font-bold text-purple-400 mb-1">{totalFIL.toFixed(6)}</p>
    <p className="text-xs text-purple-300/70">Filecoin Rewards</p>
  </div>

  {/* AR */}
  <div className="p-5 bg-gradient-to-br from-sky-900/30 to-blue-900/30 rounded-xl border border-sky-500/30 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-sky-400/50">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-sky-500/20 rounded-lg">
          <img
            src="/arweave.png"
            alt="Arweave (AR) logo"
            className="w-5 h-5 rounded-full object-cover ring-2 ring-sky-400/60 bg-white/5"
            loading="lazy"
            decoding="async"
            width="20"
            height="20"
          />
        </div>
        <span className="text-sm font-medium text-sky-300">Arweave (AR)</span>
      </div>
    </div>
    <p className="text-3xl font-bold text-sky-400 mb-1">{totalAR.toFixed(6)}</p>
    <p className="text-xs text-sky-300/70">Arweave Rewards</p>
  </div>
</div>

      </div>

      {/* Transaction History */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
          <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          Transaction History
        </h4>

        <div className="bg-gray-800/30 border border-gray-700/30 rounded-lg overflow-hidden">
          {(transactions || []).length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-700/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-gray-400 font-medium mb-1">No transactions yet</p>
              <p className="text-gray-500 text-sm">Your earnings history will appear here</p>
            </div>
          ) : (
            <div
              className={`w-full ${needsScroll ? 'max-h-96 overflow-y-auto' : 'overflow-visible'}`}
              aria-label="Transactions table container"
            >
              <table className="w-full">
                <thead className="bg-gray-800/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/30">
                  {transactions.map((tx, i) => {
                    const chain = (tx.chain || '').toUpperCase();
                    const link = txExplorerUrl(tx.chain, tx.tx_hash);
                    const isIncoming = tx.direction === 'in';

                    return (
                      <tr key={tx.tx_hash || i} className="hover:bg-gray-700/20 transition-colors duration-150">
                        {/* Type */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-md border border-gray-600/60 bg-gray-700/40 px-2 py-0.5 text-xs text-gray-200">
                              {chain || '—'}
                            </span>
                            {tx.tx_hash ? (
                              <span className="text-xs text-gray-400 font-mono">{shortHash(tx.tx_hash)}</span>
                            ) : (
                              <span className="text-xs text-gray-500">no hash</span>
                            )}
                            <span className={`text-xs ${isIncoming ? 'text-emerald-400' : 'text-red-400'}`}>
                              {isIncoming ? 'IN' : 'OUT'}
                            </span>
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3">
                          <span
                            className={`font-mono text-sm font-semibold ${
                              isIncoming ? 'text-emerald-400' : 'text-red-400'
                            }`}
                          >
                            {isIncoming ? '+' : '-'}
                            {toNum(tx.amount).toFixed(6)}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-300">
                            {tx.tx_timestamp ? new Date(tx.tx_timestamp as any).toLocaleString() : '—'}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3">
                          {tx.tx_hash ? (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors duration-150"
                            >
                              View
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarningsPanel;
